import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

/**
 * Shared renderer for admin-authored update content (Feed, Dashboard, previews).
 *
 * The admin TipTap editor saves markdown via tiptap-markdown, which has two
 * quirks the default ReactMarkdown setup mishandled (Hayley's report, 2026-06-10):
 *
 * - Line breaks inside list items serialize inconsistently — sometimes as a
 *   soft break + `\` hard-break line, sometimes as two hard breaks. Without
 *   remark-breaks the soft-break variant collapses to a space, so visually
 *   identical edits in the admin rendered with different spacing on Updates.
 * - Underline has no markdown syntax, so it serializes as literal `<u>` HTML.
 *   Without rehype-raw the tags rendered as visible text.
 */

// Allow the inline HTML the editor actually produces (<u>); strip anything unsafe.
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames ?? []), "u"],
};

/**
 * Prose classes shared by every update render site, and mirrored by the admin
 * editor so WYSIWYG matches the public page:
 * - Headings (the editor authors h3) render visibly larger than body text.
 * - List spacing is uniform whether markdown parses a list as tight or loose
 *   (loose items wrap content in <p>, which otherwise adds big margins).
 */
export const updateProseClasses =
  "prose prose-sm prose-slate max-w-none " +
  "prose-headings:text-foreground prose-headings:text-base prose-headings:font-semibold " +
  "prose-li:my-1 [&_li>p]:my-0 [&_li>p+p]:mt-2 " +
  "prose-p:text-foreground/80 prose-li:text-foreground/80 prose-strong:text-foreground " +
  "prose-em:text-foreground/70 prose-hr:border-border/60";

export function UpdateMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={[remarkBreaks]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
    >
      {children}
    </ReactMarkdown>
  );
}
